import * as Mui from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ContextOperation } from "../../../../context/OperationContext";
import useSnackbar from "../../../../hooks/useSnackbar";
import useToggle from "../../../../hooks/useToggle";
import FormInput from "../../../common/FormInput";
import { nameof } from "../../../common/ObjectUtils";
import { ContextDamageForm } from "../../DamageFormContext";
import SectionCardActions from "../../SectionCardActions";
import SectionCardHeader from "../../SectionCardHeader";
import DamageFormService from "../../service/DamageFormService";
import { DamageFormTypes } from "../../Types";
import Dialogs from "../dialogs";
import TaskStatus, { ITaskStatus } from "../TaskStatus";
import useDamageFormActions from "../../service/DamageFormActions";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import GeneralInfoCard from "../../generalInfo/GeneralInfoCard";
import { useHasScope } from "../../../../../../config/globalConfig";
import i18n from "i18next";

const Task2 = () => {
  const hasScope = useHasScope;

  const { tenant, setLoading } = useContext(ContextOperation);
  const { task2 } = useContext(ContextDamageForm);
  const damageFormContext = useContext(ContextDamageForm);

  const t = useOperationTranslation();

  const { id } = useParams();

  const snackBar = useSnackbar();

  const [openCloseConfirm, toggleCloseConfirm] = useToggle();
  const [reopenCloseConfirm, toggleReopenConfirm] = useToggle();

  const actions = useDamageFormActions();

  const form = useForm<DamageFormTypes.ITask2Update>();
  const [taskStatus, setTaskStatus] = useState<ITaskStatus>({});

  const [loadComponent, setLoadComponent] = useState(0);

  useEffect(() => {
    if (task2) return;

    actions.getTask2();
    setLoadComponent(1);
  }, []);

  const onFormReset = () => {
    form.reset({
      damageFormId: id,
      counterparty: task2?.counterparty,
      counterPartyPhone: task2?.counterpartyPhone,
      counterpartyAddress: task2?.counterpartyAddress,
      counterpartyCity: task2?.counterpartyCity,
      counterpartyProvince: task2?.counterpartyProvince,
      counterpartyRegion: task2?.counterpartyRegion,
      counterPartyModel: task2?.counterpartyModel,
      counterpartyPlate: task2?.counterpartyPlate,
      ownerPolicy: task2?.insuranceOwner,
      companyPolicy: task2?.insuranceCompany,
      witnessOne: task2?.witnessOne,
      witnessTwo: task2?.witnessTwo,
      injuredOne: task2?.injuredOneName,
      severityInjuredOne: task2?.injuredOneSeverity,
      injuredTwo: task2?.injuredTwoName,
      severityInjuredTwo: task2?.injuredTwoSeverity,
      counterpartInsuranceCompany: task2?.counterpartInsuranceCompany,
      accidentAddress: task2?.accidentAddress,
      accidentAuthority: task2?.accidentAuthority,
      accidentAuthorityPhone: task2?.accidentAuthorityPhone,
      accidentCity: task2?.accidentCity,
      accidentDateTime: task2?.accidentDate,
      accidentDynamics: task2?.accidentDynamics,
      accidentRegion: task2?.accidentRegion,
      accidentProvince: task2?.accidentProvince,
      claimSent: task2?.claimSent,
      claimDate: task2?.claimDate,
      obtainedAmount: task2?.obtainedAmount || undefined,
      insuranceClaimNumber: task2?.insuranceClaimNumber || undefined,
      lumpSumApproved: task2?.lumpSumApproved || undefined,
      redeemedAmount: task2?.redeemedAmount || undefined,
	  drivaliaInsuranceCompany: task2?.drivaliaInsuranceCompany,
      eventDate: task2?.eventDate,
	    eventCity: task2?.eventCity,
	    eventRegion: task2?.eventRegion,
	    cdwPlus: task2?.cdwPlus,
	    gt: task2?.gt,
	    requestedAmount: task2?.requestedAmount,
	    cidRcaType: task2?.cidRcaType,
	    accidentResult: task2?.accidentResult,
	    noCardAssignment: task2?.noCardAssignment,
      counterpartyFlag: task2?.counterpartyFlag,
	  theftDeductibleCharged: task2?.theftDeductibleCharged
    });
  };

  useEffect(() => {
    setTaskStatus({
      open: task2?.open,
      openingDate: task2?.createDate,
      openingUser: task2?.createUsername,
      closeDate: task2?.closeDate,
      closeUser: task2?.closeUsername,
    });

    onFormReset();
  }, [task2]);

  i18n.on('languageChanged', () => {
    actions.getTask2();
    actions.getTask3();
  });

  const onSubmit = (data: DamageFormTypes.ITask2Update) => {
    if (!id) return;

    data.damageFormId = id;

    //data.gt = data.gt * 100;
	  data.cdwPlus = convertMoney(data.cdwPlus);
    data.lumpSumApproved = convertMoney(data.lumpSumApproved);
    data.obtainedAmount = convertMoney(data.obtainedAmount);
    data.redeemedAmount = convertMoney(data.redeemedAmount);
    data.requestedAmount = convertMoney(data.requestedAmount);

    setLoading(true);
    DamageFormService.details.task2Update.put.updateTask2(data).then(
      () => {
        actions.getTask2();        
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task2.snackbar.update.success")
        );
        setLoading(false);
      },
      (error: any) => {
        if (error.response.status == 406) {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.update.error406")
          );
        } 
        else if (error.response.status == 409) {
          snackBar.show(
            "error",
            t("damageForm.details.generalInfo.snackbar.update.error409")
          );
        } else {
          snackBar.show(
            "error",
            t("damageForm.details.task2.snackbar.update.error")
          );
        }

        //data.gt = data.gt/100;
		data.cdwPlus = data.cdwPlus/100;
        data.lumpSumApproved = data.lumpSumApproved/100;
        data.obtainedAmount = data.obtainedAmount/100;
        data.redeemedAmount = data.redeemedAmount/100;
        data.requestedAmount = data.requestedAmount/100;
        setLoading(false);
      }
    );
  };

  const onCloseTask2 = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task2.put.closeById(id).then(
      () => {
        toggleCloseConfirm();
        actions.getTask2();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task2.snackbar.close.success")
        );
        setLoading(false);
      },
      () => {
        toggleCloseConfirm();
        snackBar.show(
          "error",
          t("damageForm.details.task2.snackbar.close.error")
        );
        setLoading(false);
      }
    );
  };

  const onReopen = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task2.put.reopenByid(tenant, id).then(
      () => {
        toggleReopenConfirm();
        actions.getTask2();
		actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task2.snackbar.reopen.success")
        );
        setLoading(false);
      },
      (error: any) => {
        snackBar.show(
          "error",
          t("damageForm.details.task2.snackbar.reopen.error")
        );

        toggleReopenConfirm();
        setLoading(false);
      }
    );
  };

  const convertMoney = (amount: any) => {

    var tmpAmount = '' + amount;

    if (tmpAmount.includes(',')) 
      amount = amount.replace(',', '.');

    return amount*100;

  }

  return (
    <>
      <Mui.Box mt={2}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Mui.Card>
            <SectionCardHeader>
              <Mui.Grid container spacing={2} mr={1}>
                {task2?.open &&
                  !damageFormContext.generalInfo?.migrated &&
                  hasScope("SCOPE_CLOSE_DAMAGE_DAMAGE_FORM") && (
                    <Mui.Grid item xs="auto">
                      <Mui.Button
                        variant="contained"
                        size="small"
                        startIcon={<MuiIcons.HighlightOffRounded />}
                        onClick={toggleCloseConfirm}
                      >
                        <Mui.Typography color="inherit" variant="h6">
                          {t("damageForm.details.task2.header.btnClose")}
                        </Mui.Typography>
                      </Mui.Button>
                    </Mui.Grid>
                  )}
                {!task2?.open &&
                  !damageFormContext.generalInfo?.migrated &&
                  hasScope("SCOPE_REOPEN_DAMAGE_TASK_TWO") && (
                    <Mui.Grid item xs="auto">
                      <Mui.Button
                        variant="contained"
                        size="small"
                        startIcon={<MuiIcons.HighlightOffRounded />}
                        onClick={toggleReopenConfirm}
                      >
                        <Mui.Typography color="inherit" variant="h6">
                          {t("damageForm.details.task2.header.btnReopen")}
                        </Mui.Typography>
                      </Mui.Button>
                    </Mui.Grid>
                  )}
              </Mui.Grid>
            </SectionCardHeader>

            <Mui.CardContent>
              <Mui.Stack spacing={2}>
                <GeneralInfoCard />

                <TaskStatus formInfo={taskStatus} />

                <Mui.Accordion>
                  <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />}>
                    <Mui.Typography>
                      {t("damageForm.details.task2.counterpartDetails.title")}
                    </Mui.Typography>
                  </Mui.AccordionSummary>

                  <Mui.AccordionDetails>

                  <Mui.Grid container spacing={3}>
                    <Mui.Grid item xs={48} sm={24} md={12}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartyFlag"
                          )}
                          formInstance={form}
                          type="checkbox"
                          label={t(
                            "damageForm.details.generalInfo.taskStatus.labels.counterpart"
                          )}
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                  
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterparty"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartName"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterPartyPhone"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartPhone"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartyAddress"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartyAddress"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartyCity"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartyCity"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartyProvince"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartyProvince"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartyRegion"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartyRegion"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartyPlate"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartVehiclePlate"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterPartyModel"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartCarModel"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "ownerPolicy"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.policyAccountholder"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "companyPolicy"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.policyCompany"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "counterpartInsuranceCompany"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.counterpartInsuranceCompany"
                          )}
                          fullWidth
                          disabled={
                            damageFormContext.generalInfo?.migrated ||
                            !form.watch("counterpartyFlag") ||
                            !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
                          }
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "witnessOne"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.witness1name"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={12} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "witnessTwo"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.witness2name"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "injuredOne"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.injured1name"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "severityInjuredOne"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.injured1quality"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "injuredTwo"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.injured2name"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "severityInjuredTwo"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task2.counterpartDetails.labels.injured2quality"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                    </Mui.Grid>
                  </Mui.AccordionDetails>
                </Mui.Accordion>

                <Mui.Accordion>
                  <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />}>
                    <Mui.Typography>
                      {t(
                        "damageForm.details.task1.eventDetails.titles.eventDetails"
                      )}
                    </Mui.Typography>
                  </Mui.AccordionSummary>

                  <Mui.AccordionDetails>
                    <Mui.Grid container spacing={3}>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>("cidRcaType")}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.cidRcaType"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>("cdwPlus")}
                          formInstance={form}
                          type="number"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.cdwPlus"
                          )}
                          fullWidth
                          disabled={true}

                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>("gt")}
                          formInstance={form}
                          type="checkbox"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.gt"
                          )}
                          fullWidth
                          disabled={true}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentCity"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentCity"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentAddress"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentStreet"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentProvince"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentProvince"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentRegion"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentRegion"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentDateTime"
                          )}
                          //value={props.task2Fields?.accidentDate |}
                          formInstance={form}
                          type="date"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentDate"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
					  <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentDynamics"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentDynamics"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "accidentResult"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.accidentResult"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
					  <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput 
                            name={nameof<DamageFormTypes.ITask2>("supercover")}
                            value={task2?.supercover}
                            type="checkbox"
                            label={t("damageForm.details.task1.vehicleStatus.labels.supercover")}
                            fullWidth
                            disabled
                        />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput 
                            name={nameof<DamageFormTypes.ITask2>("keyboxReturn")}
                            value={task2?.keyboxReturn}
                            type="checkbox"
                            label={t("damageForm.details.task1.vehicleStatus.labels.keyboxReturn")}
                            fullWidth
                            disabled
                        />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput 
                            name={nameof<DamageFormTypes.ITask2Update>("theftDeductibleCharged")}
                            formInstance={form}
                            type="checkbox"
                            label={t("damageForm.details.task1.eventDetails.labels.chargedDeductibles")}
                            fullWidth
                            disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                    </Mui.Grid>

                    </Mui.Grid>

                    <Mui.Divider
                      style={{ marginTop: "20px", marginBottom: "20px" }}
                    />

                    <Mui.Typography mb="20px">
                      {t(
                        "damageForm.details.task1.eventDetails.titles.accidentDetails"
                      )}
                    </Mui.Typography>

                    <Mui.Grid container spacing={3}>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "claimSent"
                          )}
                          formInstance={form}
                          type="checkbox"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.complaintSent"
                          )}
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "claimDate"
                          )}
                          formInstance={form}
                          value={task2?.claimDate}
                          type="date"
                          label={t(
                            "damageForm.details.task1.eventDetails.labels.complaintDate"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>
                    </Mui.Grid>
                  </Mui.AccordionDetails>
                </Mui.Accordion>

                <Mui.Accordion>
                  <Mui.AccordionSummary expandIcon={<MuiIcons.ExpandMore />}>
                    <Mui.Typography>
                      {t("damageForm.details.task2.reinbursementDetails.title")}
                    </Mui.Typography>
                  </Mui.AccordionSummary>

                  <Mui.AccordionDetails>
                    <Mui.Grid container spacing={3}>
                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "insuranceClaimNumber"
                          )}
                          formInstance={form}
                          type="text"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.insuranceClaimNumber"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={12} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "noCardAssignment"
                          )}
                          type="text"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.noCardAssignment"
                          )}
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                          fullWidth
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "redeemedAmount"
                          )}
                          type="number"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.redeemedImport"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2>(
                            "lumpSumApproved"
                          )}
                          type="number"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.approvedFlatRate"
                          )}
                          formInstance={form}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "requestedAmount"
                          )}
                          type="number"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.requiredImport"
                          )}
                          formInstance={form}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2Update>(
                            "obtainedAmount"
                          )}
                          type="number"
                          formInstance={form}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.obtainedImport"
                          )}
                          fullWidth
                          disabled={damageFormContext.generalInfo?.migrated ||
                                !hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")}
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2>(
                            "requestedReimbursementDate"
                          )}
                          type="date"
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.compensationRequiredDate"
                          )}
                          value={task2?.requestedReimbursementDate}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>

                      <Mui.Grid item xs={12} sm={6} md={3}>
                        <FormInput
                          name={nameof<DamageFormTypes.ITask2>(
                            "obtainedReimbursementDate"
                          )}
                          type="date"
                          value={task2?.obtainedReimbursementDate}
                          label={t(
                            "damageForm.details.task3.billingDetails.labels.compensationObtainedDate"
                          )}
                          fullWidth
                          disabled
                        />
                      </Mui.Grid>
					 
					  <Mui.Grid item xs={12} sm={6} md={3}>
						  <FormInput
							  name={nameof<DamageFormTypes.ITask2Update>(
								"drivaliaInsuranceCompany"
							  )}
							  type="text"
							  formInstance={form}
							  label={t(
								"damageForm.details.task3.billingDetails.labels.drivaliaInsuranceCompany"
							  )}
							  fullWidth
							  disabled={
								damageFormContext.generalInfo?.migrated ||
								!task2?.open ||
								!hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")
							  }
							/>
                      </Mui.Grid>
                    </Mui.Grid>
                  </Mui.AccordionDetails>
                </Mui.Accordion>
              </Mui.Stack>
            </Mui.CardContent>

            {(hasScope("SCOPE_CREATE_DAMAGE_TASK_TWO") ||
              hasScope("SCOPE_UPDATE_DAMAGE_TASK_TWO")) && (
              <SectionCardActions onClickCancel={onFormReset} />
            )}
          </Mui.Card>
        </form>
      </Mui.Box>

      <Dialogs.CloseTaskConfirmation
        open={openCloseConfirm}
        onClose={toggleCloseConfirm}
        taskName={t("damageForm.details.tabs.task2")}
        onCloseTask={onCloseTask2}
      />

      <Dialogs.ReopenTaskConfirmation
        open={reopenCloseConfirm}
        onReopen={toggleReopenConfirm}
        onReopenTask={onReopen}
        taskName={t("damageForm.details.tabs.task2")}
      />
    </>
  );
};
export default Task2;
